/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("MatrixController", ["ControllerBase", "ControllerFactory"], function (context, cls) {
  /**
   * @class MatrixController
   * @memberOf classes
   * @extends classes.ControllerBase
   */
  class MatrixController extends cls.ControllerBase /** @lends classes.MatrixController.prototype */ {
    static __name = "MatrixController";

    // _currentRow is assigned via setCurrentRow() during _initBehaviors (invoked
    // during super() construction), so it is intentionally not declared as a class
    // field: a field initializer would run after super() and clobber that value.

    /**
     * true if value list has been initialized and created manually next to a delayed attribute change
     */
    _isValueListCreationForced = false;

    _initBehaviors() {
      super._initBehaviors();

      this.setCurrentRow(this.getAnchorNode().attribute("currentRow"));
      // pseudo-selector behaviors
      this._addBehavior(cls.FocusCurrentCellPseudoSelectorBehavior);
      this._addBehavior(cls.OffsetPseudoSelectorBehavior);
      this._addBehavior(cls.HiddenVMBehavior);
    }

    /**
     * @inheritDoc
     */
    setFocus(preventScroll = false) {
      const widget = this.getCurrentInternalWidget();
      if (widget) {
        if (this.isInScrollGrid()) {
          const scrollGridNode = this.getAnchorNode().getAncestor("ScrollGrid");
          const scrollGridController = scrollGridNode.getController();
          if (scrollGridController.getWidget() && scrollGridController.getWidget().activateRowBound) {
            scrollGridController.getWidget().activateRowBound();
          }
          // show filter when matrix is in scrollgrid
          scrollGridController.showReduceFilter();
        }
        widget.setFocus(false, preventScroll);
      } else {
        const appWidget = context.SessionService.getCurrent().getCurrentApplication().getUI().getWidget();
        if (appWidget) {
          const uiWidget = appWidget._uiWidget;
          if (uiWidget) {
            uiWidget.setFocus();
          }
        }
      }
    }

    setCurrentRow(currentRow) {
      this._currentRow = currentRow;
    }

    getCurrentRow() {
      return this._currentRow;
    }

    /** This method have no sense */
    updateAllSiblingMatrixCurrentRow(currentRow, updateCss, isScrollGrid) {
      const matrixParent = this.getAnchorNode().getParentNode();
      if (!matrixParent) {
        return;
      }

      const childrenMatrix = matrixParent.getChildren("Matrix");
      if (!childrenMatrix) {
        return;
      }

      for (const colMatrix of childrenMatrix) {
        // update processing current row
        const matrixCtrl = colMatrix.getController();
        if (!matrixCtrl) {
          continue;
        }

        const previousRow = matrixCtrl.getCurrentRow();
        if (matrixCtrl && matrixCtrl.setCurrentRow) {
          matrixCtrl.setCurrentRow(currentRow);
        }

        // update current row css
        if (!updateCss) {
          continue;
        }

        const valueList = colMatrix.getFirstChild("ValueList");
        const widgets = valueList ? colMatrix.getFirstChild("ValueList").getChildren() : [];

        if (currentRow >= widgets.length || previousRow >= widgets.length) {
          continue;
        }

        const previousWidget = widgets[previousRow];
        if (previousWidget) {
          previousWidget.getController().getWidget().removeClass("gbc_isCurrentRow");
        }
        const newWidget = widgets[currentRow];
        if (!newWidget) {
          continue;
        }
        let addCurrentRowOnField = true;
        const matrixParentWidget = matrixParent.getController().getWidget();
        if (isScrollGrid && matrixParentWidget.hasFocusOnField) {
          const matrixNode = this.getAnchorNode();
          const uiNode = matrixParent.getApplication().uiNode();
          const hasFocus = matrixNode.getId() === uiNode.attribute("focus");

          const isDisplayArray = matrixNode.attribute("dialogType") === "DisplayArray";

          if (isDisplayArray && matrixParentWidget.hasFocusOnField()) {
            addCurrentRowOnField = addCurrentRowOnField && matrixParentWidget.isHighlightCurrentCell();
          } else if (!isDisplayArray && !matrixParentWidget.isHighlightCurrentRow()) {
            addCurrentRowOnField = addCurrentRowOnField && hasFocus && matrixParentWidget.isHighlightCurrentCell();
          }
        }
        newWidget.getController().getWidget().toggleClass("gbc_isCurrentRow", addCurrentRowOnField);
      }
    }

    /**
     * Sends the updated value to the DVM
     * @private
     */
    sendWidgetValue(newValue = null) {
      const valueNode = this.getAnchorNode().getCurrentValueNode(true);
      if (valueNode) {
        const ctrl = valueNode.getController();
        if (ctrl) {
          ctrl.sendWidgetValue(newValue);
        }
      }
    }
  }

  cls.MatrixController = MatrixController;
  cls.ControllerFactory.register("Matrix", cls.MatrixController);
});
